// The state machine of a task, as coordinates.
//
// WHY THIS IS THE RIGHT PICTURE FOR A LIVE SCREEN. Every notice the channel
// sends is one transition — `task.claimed` IS the edge from `ready` to
// `claimed`, not a description of one. So the graph is not an illustration
// drawn next to the data: the edges and the event kinds are the same list, and
// an edge nobody can light up is a transition the backend cannot emit.
//
// The states come from WORKFLOW_STATES in data/models/work/task.js and the
// transitions from what the managers record. Both were read, not assumed.
//
// `returned` IS A STATE IN THE ENUM AND NO NODE HERE. ReviewManager sends a
// returned task back to `ready`, not to `returned`, so nothing ever lands
// there — the board counts 0 and always will. Drawing it would be drawing a
// room with no door.
//
// ORTHOGONAL, NOT CURVED, and the labels are placed rather than flowed. A
// `textPath` follows its path's direction, so every backwards edge came out
// mirrored and every vertical one came out sideways — legible only with your
// head turned. Right angles also let two lanes cross at 90°, which reads as a
// crossing instead of as a knot.

// The canvas the paths are drawn in. Everything below is in these units and
// the SVG scales to its container, so no number here is a pixel on screen.
export const CANVAS = { width: 1480, height: 540 };

const NODE = { width: 190, height: 96 };
const ROW = 200;                      // the main line of the flow
const EXIT_ROW = 420;                 // where a task goes when it stops
const MID = ROW + NODE.height / 2;    // 248 — where the forward edges run
const FOOT = ROW + NODE.height;       // 296 — the bottom of the main row

// The two lanes that carry work backwards, and the shallow bump the heartbeat
// uses. Separated on purpose: two lines sharing a lane is two lines nobody can
// follow.
const LANE_RETURNED = 86;
const LANE_RELEASED = 140;
const LANE_BEAT = 172;

// Where the way back from `blocked` runs: under the main row, clear of the two
// drops, so it reads as leaving the exit row and not as another exit.
const LANE_UNBLOCKED = EXIT_ROW + NODE.height / 2;   // 468 — mid-height of the exit row

// Left to right, the way work actually travels.
export const NODES = [
    { state: 'proposed',  label: 'Propuesta',   hint: 'Anotada, sin decidir',   x: 40,   y: ROW },
    { state: 'ready',     label: 'Lista',       hint: 'En la cola',             x: 340,  y: ROW },
    { state: 'claimed',   label: 'Tomada',      hint: 'Un agente la tiene',     x: 640,  y: ROW },
    { state: 'in_review', label: 'En revisión', hint: 'Espera a una persona',   x: 940,  y: ROW },
    { state: 'done',      label: 'Hecha',       hint: 'Aceptada',               x: 1240, y: ROW },
    { state: 'blocked',   label: 'Bloqueada',   hint: 'Con razón escrita',      x: 640,  y: EXIT_ROW },
    { state: 'discarded', label: 'Descartada',  hint: 'Desde cualquier estado', x: 1090, y: EXIT_ROW },
];

export const NODE_SIZE = NODE;

const byState = Object.fromEntries(NODES.map(node => [node.state, node]));

const rightOf  = state => byState[state].x + NODE.width;
const leftOf   = state => byState[state].x;
const centerOf = state => byState[state].x + NODE.width / 2;

// Back from the exit row: out of the left side of the source, along the lane,
// up into the bottom of the target. `entry` sits left of the target's center
// so the arrowhead does not land where the `blocked` drop leaves `claimed`.
function rise(from, to, entry) {
    const start = leftOf(from);
    return { d: `M ${start} ${LANE_UNBLOCKED} H ${entry} V ${FOOT}`, label: {
        x: (start + entry) / 2, y: LANE_UNBLOCKED - 12, anchor: 'middle',
    } };
}

// A forward hop: a straight run along the row, because both ends sit on it.
function forward(from, to) {
    return { d: `M ${rightOf(from)} ${MID} H ${leftOf(to)}`, label: {
        x: (rightOf(from) + leftOf(to)) / 2, y: MID - 12, anchor: 'middle',
    } };
}

// Backwards: up out of the source, along a lane, down into the target. `entry`
// is where it lands on the target's top edge — two returns arriving at the same
// point would look like one.
function backward(from, to, lane, entry) {
    const start = centerOf(from);
    return { d: `M ${start} ${ROW} V ${lane} H ${entry} V ${ROW}`, label: {
        x: (entry + start) / 2, y: lane - 10, anchor: 'middle',
    } };
}

// Straight down, to an exit. The label sits BESIDE the line and stays
// horizontal; along it, it would read top to bottom.
function drop(fromX) {
    return { d: `M ${fromX} ${FOOT} V ${EXIT_ROW}`, label: {
        x: fromX + 12, y: (FOOT + EXIT_ROW) / 2, anchor: 'start',
    } };
}

// The heartbeat leaves and comes back to the same node, so it is a loop and not
// a hop. Without it the screen would suggest that a task in `claimed` is silent
// until it is delivered, when `task.progress` is what keeps the reservation
// from expiring.
function loop(state) {
    const cx = centerOf(state);
    return { d: `M ${cx - 45} ${ROW} V ${LANE_BEAT} H ${cx + 45} V ${ROW}`, label: {
        x: cx, y: LANE_BEAT - 10, anchor: 'middle',
    } };
}

// `kind` is the event the backend emits. `from`/`to` are what the count moves
// by when one arrives — null means the transition can start anywhere, so
// nothing is subtracted.
function edge(kind, from, to, shape, flags = {}) {
    return { kind, from, to, path: shape.d, label: shape.label, ...flags };
}

// `task.authorized` IS the move from `proposed` to `ready`: since T02.04 every
// authorization writes it, one per task. `task.promoted` is NOT an edge any
// more — the single-task route writes it IN ADDITION, for the same move, to
// record the priority it set. Drawing both would count one task twice.
export const EDGES = [
    edge('task.authorized', 'proposed', 'ready',     forward('proposed', 'ready')),
    edge('task.claimed',   'ready',     'claimed',   forward('ready', 'claimed')),
    edge('task.delivered', 'claimed',   'in_review', forward('claimed', 'in_review')),
    edge('task.accepted',  'in_review', 'done',      forward('in_review', 'done')),
    // Lands left of where `released` lands, and drops outside its lane, so the
    // two never cross and never share an arrowhead.
    edge('task.returned',  'in_review', 'ready',     backward('in_review', 'ready', LANE_RETURNED, 400), { back: true }),
    edge('task.released',  'claimed',   'ready',     backward('claimed', 'ready', LANE_RELEASED, 460), { back: true }),
    edge('task.blocked',   'claimed',   'blocked',   drop(centerOf('claimed')), { exit: true }),
    // Blocking is not a dead end (T16.4): a person unblocks it with a reason,
    // and it goes back to the queue with the priority it had.
    edge('task.unblocked', 'blocked',   'ready',     rise('blocked', 'ready', 435), { back: true }),
    // Starts in the empty space under the row and not at a box: discarding
    // happens from ANY state, and an arrow leaving one node would say it only
    // happens from that one.
    edge('task.discarded', null,        'discarded', drop(centerOf('discarded')), { exit: true }),
    edge('task.progress',  'claimed',   'claimed',   loop('claimed'), { beat: true }),
];

export const EDGE_KINDS = EDGES.map(item => item.kind);

// WHAT THE CHANNEL IS ASKED FOR — the family, not the ten names above, and
// this is not a preference.
//
// T15.4 validates `?types=` against the kinds that HAVE OCCURRED (a
// `SELECT DISTINCT kind FROM events`), so a transition that is perfectly legal
// and simply has not happened yet comes back as an unknown type and the whole
// subscription is refused with a 400. Asked for the ten explicit kinds, this
// screen gets: «No hay eventos de: task.released, task.discarded» — and the
// channel never opens.
//
// It also keeps `task.promoted` arriving: it is no edge, but it is a movement
// the list below the graph has to show.
//
// The family passes because it is a prefix and not a lookup. The cost is the
// mirror image of the explicit list: the server may send a `task.` kind this
// client has no listener for, and it would be dropped in silence. Ten names
// against one prefix, and the prefix is the one that works today.
export const STREAM_TYPES = 'task.*';
// WHAT THIS CLIENT LISTENS FOR. One listener per name (see useEventStream), so
// a kind missing here is a kind that never reaches the list, whatever the
// subscription lets through. The edges, plus the movements that are no edge.
export const LISTENED_KINDS = [...EDGE_KINDS, 'task.promoted'];

export const COUNTED_STATES = NODES.map(node => node.state);

export function edgeByKind(kind) {
    return EDGES.find(item => item.kind === kind) ?? null;
}

// What a notice does to the counts. Empty for anything the graph cannot
// attribute.
//
// `task.claimed` is the one that can lie: the reservation query also takes a
// task that is ALREADY `claimed` whose `claimed_until` expired, so now and then
// this subtracts from `ready` a task that was not there. It is rare, it is off
// by one, and it is why the header says when the counting was done and offers
// to count again — a number that quietly drifts with no way back is worse than
// one that admits it.
export function countDeltas(kind) {
    const item = edgeByKind(kind);
    if (!item) return {};
    if (item.from === item.to) return {};          // the heartbeat moves nothing

    const deltas = {};
    if (item.from) deltas[item.from] = -1;
    if (item.to) deltas[item.to] = 1;
    return deltas;
}
